import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { swapAdmissionTerms } from "./accountsApi";

const termLabel = (n) => `Term T${n}`;

const TermSwapDialog = ({
  open,
  onClose,
  clientAccountDetailId,
  swappableTerms = [],
  hasPaidInvoiceWarning = false,
  onSuccess,
}) => {
  const [termA, setTermA] = useState("");
  const [termB, setTermB] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const termBOptions = useMemo(
    () => swappableTerms.filter((n) => String(n) !== String(termA)),
    [swappableTerms, termA]
  );

  const resetForm = () => {
    setTermA("");
    setTermB("");
    setConfirmed(false);
    setError("");
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async () => {
    const a = Number(termA);
    const b = Number(termB);
    if (!a || !b || a === b) {
      setError("Select two different imported terms.");
      return;
    }
    if (!confirmed) {
      setError("Confirm you understand this action before continuing.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await swapAdmissionTerms(clientAccountDetailId, { termA: a, termB: b });
      if (!response?.success) {
        throw new Error(response?.message || "Term swap failed");
      }
      onSuccess?.(response);
      resetForm();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Term swap failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center gap-2">
        <SwapHoriz color="primary" />
        Swap imported terms
      </DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        <Typography variant="body2" color="text.secondary">
          Exchanges schedule, payments, notes, commission, bonus, and claim status between two
          imported terms. Unimported terms are not eligible.
        </Typography>

        {hasPaidInvoiceWarning && (
          <Alert severity="warning">
            One or both selected terms may have paid or partially paid provider invoices. Invoice
            line items will not be updated automatically — review provider invoices after swapping.
          </Alert>
        )}

        <FormControl fullWidth size="small">
          <InputLabel id="term-swap-a-label">First term</InputLabel>
          <Select
            labelId="term-swap-a-label"
            label="First term"
            value={termA}
            onChange={(e) => {
              setTermA(e.target.value);
              if (String(e.target.value) === String(termB)) setTermB("");
            }}
          >
            {swappableTerms.map((n) => (
              <MenuItem key={`a-${n}`} value={String(n)}>
                {termLabel(n)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!termA}>
          <InputLabel id="term-swap-b-label">Second term</InputLabel>
          <Select
            labelId="term-swap-b-label"
            label="Second term"
            value={termB}
            onChange={(e) => setTermB(e.target.value)}
          >
            {termBOptions.map((n) => (
              <MenuItem key={`b-${n}`} value={String(n)}>
                {termLabel(n)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {termA && termB && (
          <Alert severity="info">
            Will exchange all term data between {termLabel(termA)} and {termLabel(termB)}. A swap
            audit note will be added to both installments.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label="I understand this will swap payments, notes, commission, bonus, and schedule data between the selected terms."
        />

        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting || !termA || !termB || !confirmed}
        >
          {submitting ? "Swapping…" : "Swap terms"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermSwapDialog;
